import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { authAPI, settingsAPI } from "../services/api";

interface CompanySettings {
  name: string;
  logo: string | null;
  slogan: string;
}

interface Permission {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  phone: string;
  role: "admin" | "manager" | "user";
  avatar: string | null;
  permissions: Record<string, Permission>;
}

interface AppContextType {
  company: CompanySettings;
  setCompany: (s: CompanySettings) => void;
  currentUser: User | null;
  setCurrentUser: (u: User | null) => void;
  isLoggedIn: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const defaultCompany: CompanySettings = {
  name: "سیستم مدیریت",
  logo: null,
  slogan: "Enterprise v1.0",
};

export const AppContext = createContext<AppContextType>({
  company: defaultCompany,
  setCompany: () => {},
  currentUser: null,
  setCurrentUser: () => {},
  isLoggedIn: false,
  loading: true,
  logout: async () => {},
  refreshSettings: async () => {},
});

export const useApp = () => useContext(AppContext);

export function AppProvider({ children }: { children: ReactNode }) {
  const [company, setCompanyState] = useState<CompanySettings>(defaultCompany);
  const [currentUser, setCurrentUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSettings = async () => {
    try {
      const res = await settingsAPI.get();
      if (res.success) {
        const s: CompanySettings = {
          name:   res.settings.company_name   || defaultCompany.name,
          slogan: res.settings.company_slogan || defaultCompany.slogan,
          logo:   res.settings.company_logo   || null,
        };
        setCompanyState(s);
      }
    } catch {}
  };

  useEffect(() => {
    const verify = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await authAPI.verify();
        if (res.success) {
          setCurrentUserState(res.user);
          await refreshSettings();
        } else {
          localStorage.removeItem("auth_token");
        }
      } catch {
        localStorage.removeItem("auth_token");
      }
      setLoading(false);
    };
    verify();
  }, []);

  useEffect(() => {
    if (currentUser) refreshSettings();
  }, [currentUser]);

  const setCompany = (s: CompanySettings) => {
    setCompanyState(s);
  };

  const setCurrentUser = (u: User | null) => {
    setCurrentUserState(u);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {}
    localStorage.removeItem("auth_token");
    setCurrentUserState(null);
    setCompanyState(defaultCompany);
  };

  const isLoggedIn = currentUser !== null;

  return (
    <AppContext.Provider value={{
      company, setCompany,
      currentUser, setCurrentUser,
      isLoggedIn, loading,
      logout, refreshSettings,
    }}>
      {children}
    </AppContext.Provider>
  );
}